import Pool from './pool';
import Plugins from './plugins';
import DataProxy from './data-proxy';
import Table from './controllers/table';
import Headers from './controllers/headers';
import ShiftPlugin from './plugins/shift';
import ReplacePlugin from './plugins/replace';
import ReplaceTablePlugin from './plugins/replace-table';
import SwitchUnits from './plugins/switch-units';
import ShiftOrReplace from './plugins/shift-or-replace';
import ChangeClassName from './plugins/change-class-name';
import ExpandTooltipPlugin from './plugins/expand-tooltip';
import SwellTooltipPlugin from './plugins/swell-tooltip';
import ChandeModePlugin from './plugins/change-mode';
import WindStateTooltipPlugin from './plugins/wind-state-tooltip';
import TooltipPlugin from './plugins/tooltip';
import makeColumns from './helpers/make-columns';
import splitRowsByColumns from './helpers/split-rows-by-columns';
import TableIsRunningError from './errors/is-running';
import TableNoTableNodeError from './errors/no-table';
import dispatchGlobalEvent from './helpers/forecast-expanded-global-event';

class TableController {
  constructor(tables, { initialMode }) {
    this.columns = {};
    this.tables = { ...tables };
    this.headers = { ...tables };
    this.isRunning = false;

    this.$pool = new Pool();
    this.$dataProxy = new DataProxy();
    this.$dataProxy.set(['initialMode'], initialMode);

    this.$plugins = new Plugins(this.$pool, this.$dataProxy, this)
      .add('ShiftPlugin', ShiftPlugin)
      .add('ReplacePlugin', ReplacePlugin)
      .add('ShiftOrReplace', ShiftOrReplace)
      .add('ChangeClassName', ChangeClassName)
      .add('ReplaceTablePlugin', ReplaceTablePlugin)
      .add('SwellTooltipPlugin', SwellTooltipPlugin)
      .add('ExpandTooltipPlugin', ExpandTooltipPlugin)
      .add('ChandeModePlugin', ChandeModePlugin)
      .add('TooltipPlugin', TooltipPlugin)
      .add('SwitchUnits', SwitchUnits)
      .add('WindStateTooltip', WindStateTooltipPlugin);
  }

  makeColumns(forecastTable) {
    const heads = Array.from(
      forecastTable.querySelectorAll('[data-column-head]')
    );

    this.columns = makeColumns(heads);

    return this;
  }

  splitRowsByColumns(rows) {
    const columns = Object.values(this.columns);
    if (!columns.length) throw new Error('No columns');

    return splitRowsByColumns(this.columns, rows);
  }

  connectContent(tableName, node, columns) {
    this.tables[tableName] = new Table(
      this.$pool,
      this.$plugins,
      tableName
    ).connect(node, columns);

    this.$plugins.emit('Table:connectContent', {
      tableName,
      node,
    });

    this.$pool.exec('Table:connectContent::idle', null, err => {
      if (err) throw err;

      this.$pool.exec('Table:connectContent::finish', null, error => {
        if (error) throw error;
      });
    });

    return this;
  }

  connectHeaders(tableName, node, headers) {
    this.headers[tableName] = new Headers(
      this.$pool,
      this.$plugins,
      tableName
    ).connect(node, headers);

    this.$plugins.emit('Table:connectHeaders', {
      tableName,
      node,
    });

    this.$pool.exec('Table:connectHeaders::idle', null, err => {
      if (err) throw err;

      this.$pool.exec('Table:connectHeaders::finish', null, error => {
        if (error) throw error;
      });
    });

    return this;
  }

  fetchData(next) {
    this.$dataProxy.fetchAllTables((err, dataset) => {
      if (err) {
        next(err);
        return;
      }

      next(null, dataset);
    });
  }

  changeMode(columnName, mode, newMode, next) {
    if (this.isRunning) {
      next(
        new TableIsRunningError({
          method: 'changeMode',
          message: `The TableController is already proceeding an op`,
        })
      );
      return;
    }

    this.isRunning = true;

    try {
      Object.values(this.tables).forEach(table => {
        if (table.changeMode)
          table.changeMode(columnName, mode, newMode, this.$dataProxy, next);
      });
    } catch (error) {
      next(error);
      this.isRunning = false;
      return;
    }

    this.$pool.exec('Table:changeMode::idle', 'idle', err => {
      if (err) {
        next(err);
        return;
      }

      this.$pool.exec('Table:changeMode::mutate', 'mutate', error => {
        if (error) {
          next(error);
          return;
        }

        this.isRunning = false;
        dispatchGlobalEvent({ columnName, mode, newMode });
        next(null);
      });
    });
  }

  replaceTable(tableName, newTableName, next) {
    if (this.isRunning) {
      next(
        new TableIsRunningError({
          method: 'replaceTable',
          message: `The TableController is already proceeding an op`,
        })
      );
      return;
    }

    if (!this.tables[newTableName].node) {
      next(
        new TableNoTableNodeError({
          tableName,
          newTableName,
          message: 'You should connect the table before',
        })
      );
      return;
    }

    this.isRunning = true;

    this.$plugins.emit('Table:replaceTable', {
      tableName,
      newTableName,
      tables: {
        [tableName]: this.tables[tableName].node,
        [newTableName]: this.tables[newTableName].node,
      },
    });

    this.$pool.exec('Table:replaceTable::idle', 'idle', err => {
      if (err) {
        this.isRunning = false;
        next(err);
        return;
      }

      this.$pool.exec('Table:replaceTable::mutate', 'mutate', error => {
        this.isRunning = false;

        if (error) {
          next(error);
          return;
        }

        next(null);
      });
    });
  }
}

export default TableController;
